/**
 * /erp/products/{id} — view + edit + delete a product (Manager+).
 *
 * Inline edit: each editable field has a small pencil that swaps the
 * cell for an input + save/cancel — same pattern as the Tags admin
 * page. Delete is a confirm-then-destructive action; the server
 * refuses if the product is in use anywhere (PR-C and beyond will
 * extend the in-use check; for PR-A nothing references products yet).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, History, Loader2, Pencil, Save, Trash2, X } from "lucide-react";
import { useState } from "react";

import {
  ApiError,
  type ErpProduct,
  type ErpProductUpdate,
  erpApi,
  isErpCatalogConflict,
} from "../lib/api";
import {
  ERP_QUERY_KEYS,
  groupUomsByCategory,
  useProduct,
  useRawMaterials,
  useUoms,
} from "../lib/erp";
import { toast } from "../lib/toast";
import { useAuthStore } from "../lib/auth";
import { CustomFieldsPanel } from "../components/custom-fields/CustomFieldsPanel";

export const Route = createFileRoute("/_app/erp/products/$productId")({
  component: ProductDetailPage,
});

function ProductDetailPage() {
  const { productId } = useParams({ from: "/_app/erp/products/$productId" });
  const productQ = useProduct(productId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isProduction = useAuthStore((state) => state.user?.role === "production");

  const removeMutation = useMutation({
    mutationFn: () => erpApi.products.remove(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.products });
      queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.rawMaterials });
      toast.success("Product deleted.");
      navigate({ to: "/erp/products" });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not delete.";
      toast.error(msg);
    },
  });

  if (productQ.isLoading) {
    return <div className="mx-auto max-w-3xl p-6 text-sm opacity-60">Loading…</div>;
  }
  if (productQ.error) {
    return (
      <div className="mx-auto max-w-3xl p-6 text-sm text-red-700 dark:text-red-300">
        Could not load product: {(productQ.error as Error).message}
      </div>
    );
  }
  const product = productQ.data;
  if (!product) return null;

  const handleDelete = () => {
    const ok = window.confirm(`Delete product ${product.code}? This cannot be undone.`);
    if (ok) removeMutation.mutate();
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <header className="space-y-2">
        <Link
          to="/erp/products"
          className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100"
        >
          <ArrowLeft className="size-3" aria-hidden />
          Back to products
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <h1 className="truncate text-2xl font-semibold tracking-tight">{product.name}</h1>
            <p className="flex items-center gap-2 font-mono text-xs opacity-70">
              {product.code}
              <span className="rounded-full bg-[var(--hover)] px-2 py-0.5 font-sans font-medium">
                v{product.version}
              </span>
            </p>
          </div>
          {!isProduction && (
            <div className="flex items-center gap-2">
              <button
              type="button"
              onClick={handleDelete}
              disabled={removeMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/70"
            >
              {removeMutation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="size-3.5" aria-hidden />
              )}
                Delete
              </button>
            </div>
          )}
        </div>
      </header>

      <ProductEditor product={product} isProduction={isProduction} />
      {!isProduction && <ProductVersionHistory productId={product.id} />}
      <CustomFieldsPanel entity="products" recordKey={product.id} />
    </div>
  );
}

function ProductVersionHistory({ productId }: { productId: string }) {
  const versionsQ = useQuery({
    queryKey: ["erp", "products", productId, "versions"],
    queryFn: () => erpApi.products.versions(productId),
  });
  return (
    <section className="rounded-xl border border-[var(--border)] p-5">
      <h2 className="flex items-center gap-2 font-semibold">
        <History className="size-4 opacity-70" /> Version history
      </h2>
      <p className="mt-1 text-xs opacity-60">
        Each saved change creates a permanent product revision.
      </p>
      {versionsQ.isLoading ? (
        <p className="mt-4 text-sm opacity-60">Loading versions…</p>
      ) : versionsQ.error ? (
        <p className="mt-4 text-sm text-red-600">Could not load version history.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {(versionsQ.data?.items ?? []).map((revision) => (
            <details key={revision.id} className="rounded-md border border-[var(--border)] px-3 py-2">
              <summary className="cursor-pointer text-sm font-medium">
                v{revision.version} · {revision.name}
                <span className="ml-2 text-xs font-normal opacity-60">
                  {new Date(revision.created_at).toLocaleString()}
                </span>
              </summary>
              <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                <div><dt className="opacity-60">Category</dt><dd>{revision.category ?? "—"}</dd></div>
                <div><dt className="opacity-60">UOM</dt><dd>{revision.uom}</dd></div>
                <div><dt className="opacity-60">Default MRP</dt><dd>{revision.default_sell_price ?? "—"}</dd></div>
                <div><dt className="opacity-60">Status</dt><dd>{revision.is_active ? "Active" : "Inactive"}</dd></div>
                <div className="sm:col-span-2"><dt className="opacity-60">Notes</dt><dd>{revision.notes ?? "—"}</dd></div>
              </dl>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}

function ProductEditor({ product, isProduction }: { product: ErpProduct; isProduction: boolean }) {
  const queryClient = useQueryClient();
  const uomsQ = useUoms();
  const rawsQ = useRawMaterials();

  const updateMutation = useMutation({
    mutationFn: (body: ErpProductUpdate) => erpApi.products.update(product.id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.products });
      queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.product(product.id) });
      queryClient.invalidateQueries({ queryKey: ["erp", "products", product.id, "versions"] });
      queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.rawMaterials });
      toast.success("Saved.");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409 && isErpCatalogConflict(err.detail)) {
        toast.error(err.detail.message);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not save.";
      toast.error(msg);
    },
  });

  const groupedUoms = groupUomsByCategory(uomsQ.data?.items ?? []);
  const availableRaws = (rawsQ.data?.items ?? []).filter(
    (r) => r.is_active || r.id === product.linked_raw_material_id,
  );

  return (
    <div className="space-y-3 rounded-xl border border-[var(--border)] p-5">
      {!isProduction && (
      <Row label="Status">
        <button
          type="button"
          onClick={() => updateMutation.mutate({ is_active: !product.is_active })}
          disabled={updateMutation.isPending}
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            product.is_active
              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-900/60"
              : "bg-black/10 opacity-80 hover:bg-black/15 dark:bg-white/10 dark:hover:bg-white/15"
          }`}
        >
          {product.is_active ? "Active — click to deactivate" : "Inactive — click to activate"}
        </button>
      </Row>
      )}

      <InlineTextField
        label="Name"
        value={product.name}
        onSave={(v) => updateMutation.mutate({ name: v })}
        pending={updateMutation.isPending}
      />

      <InlineTextField
        label="Category"
        value={product.category ?? ""}
        placeholder="—"
        onSave={(v) => updateMutation.mutate({ category: v.trim() ? v.trim() : null })}
        pending={updateMutation.isPending}
      />

      <InlineSelectField
        label="Unit of measure"
        value={product.uom}
        groups={groupedUoms}
        onSave={(v) => updateMutation.mutate({ uom: v })}
        pending={updateMutation.isPending}
      />

      {!isProduction && (
        <>
      <InlineTextField
        label="Default sell price"
        value={product.default_sell_price ?? ""}
        placeholder="—"
        type="number"
        onSave={(v) => updateMutation.mutate({ default_sell_price: v.trim() ? v.trim() : null })}
        pending={updateMutation.isPending}
      />

      <Row label="Linked raw material">
        <LinkedRawSelector
          currentId={product.linked_raw_material_id}
          options={availableRaws}
          onChange={(v) => updateMutation.mutate({ linked_raw_material_id: v })}
          pending={updateMutation.isPending}
        />
      </Row>

      <InlineTextField
        label="Notes"
        value={product.notes ?? ""}
        placeholder="—"
        multiline
        onSave={(v) => updateMutation.mutate({ notes: v.trim() ? v.trim() : null })}
        pending={updateMutation.isPending}
      />

        </>
      )}

      <Row label="Created">
        <span className="text-xs opacity-60">{new Date(product.created_at).toLocaleString()}</span>
      </Row>
    </div>
  );
}

// ── Row primitives ──────────────────────────────────────────────────────────

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[10rem_1fr]">
      <div className="pt-1 text-xs font-medium opacity-70">{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function InlineTextField({
  label,
  value,
  onSave,
  pending,
  type = "text",
  placeholder,
  multiline,
}: {
  label: string;
  value: string;
  onSave: (v: string) => void;
  pending: boolean;
  type?: "text" | "number";
  placeholder?: string;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const startEdit = () => {
    setDraft(value);
    setEditing(true);
  };
  const cancel = () => setEditing(false);
  const submit = () => {
    if (draft === value) {
      setEditing(false);
      return;
    }
    onSave(draft);
    setEditing(false);
  };

  return (
    <Row label={label}>
      {editing ? (
        <div className="flex items-start gap-2">
          {multiline ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
            />
          ) : (
            <input
              type={type}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
            />
          )}
          <button
            type="button"
            onClick={submit}
            disabled={pending}
            aria-label="Save"
            className="rounded-md border border-emerald-300 p-1 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 dark:border-emerald-900 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
          >
            {pending ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <Save className="size-3.5" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={cancel}
            aria-label="Cancel"
            className="rounded-md border border-[var(--border)] p-1 hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <span className="flex-1 text-sm">
            {value || <span className="opacity-50">{placeholder ?? "—"}</span>}
          </span>
          <button
            type="button"
            onClick={startEdit}
            aria-label={`Edit ${label}`}
            className="rounded-md border border-[var(--border)] p-1 opacity-50 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/5"
          >
            <Pencil className="size-3.5" aria-hidden />
          </button>
        </div>
      )}
    </Row>
  );
}

function InlineSelectField({
  label,
  value,
  groups,
  onSave,
  pending,
}: {
  label: string;
  value: string;
  groups: { category: string; items: { code: string; label: string }[] }[];
  onSave: (v: string) => void;
  pending: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  return (
    <Row label={label}>
      {editing ? (
        <div className="flex items-start gap-2">
          <select
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
          >
            {groups.map((g) => (
              <optgroup key={g.category} label={g.category}>
                {g.items.map((i) => (
                  <option key={i.code} value={i.code}>
                    {i.code} — {i.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              if (draft !== value) onSave(draft);
              setEditing(false);
            }}
            disabled={pending}
            className="rounded-md border border-emerald-300 p-1 text-emerald-700 disabled:opacity-50 dark:border-emerald-900 dark:text-emerald-300"
          >
            {pending ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <Save className="size-3.5" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="rounded-md border border-[var(--border)] p-1"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <span className="flex-1 text-sm">{value}</span>
          <button
            type="button"
            onClick={() => {
              setDraft(value);
              setEditing(true);
            }}
            aria-label={`Edit ${label}`}
            className="rounded-md border border-[var(--border)] p-1 opacity-50 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/5"
          >
            <Pencil className="size-3.5" aria-hidden />
          </button>
        </div>
      )}
    </Row>
  );
}

function LinkedRawSelector({
  currentId,
  options,
  onChange,
  pending,
}: {
  currentId: string | null;
  options: { id: string; code: string; name: string }[];
  onChange: (v: string | null) => void;
  pending: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentId ?? "");

  const current = options.find((o) => o.id === currentId);

  if (editing) {
    return (
      <div className="flex items-start gap-2">
        <select
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
        >
          <option value="">— Not linked —</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.code} — {o.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            const next = draft || null;
            if (next !== currentId) onChange(next);
            setEditing(false);
          }}
          disabled={pending}
          className="rounded-md border border-emerald-300 p-1 text-emerald-700 disabled:opacity-50 dark:border-emerald-900 dark:text-emerald-300"
        >
          {pending ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
          ) : (
            <Save className="size-3.5" aria-hidden />
          )}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="rounded-md border border-[var(--border)] p-1"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2">
      <span className="flex-1 text-sm">
        {current ? (
          <>
            <span className="font-mono text-xs opacity-70">{current.code}</span>{" "}
            <span>{current.name}</span>
          </>
        ) : (
          <span className="opacity-50">Not linked</span>
        )}
      </span>
      <button
        type="button"
        onClick={() => {
          setDraft(currentId ?? "");
          setEditing(true);
        }}
        aria-label="Edit linked raw material"
        className="rounded-md border border-[var(--border)] p-1 opacity-50 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/5"
      >
        <Pencil className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}
